import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowRight } from "lucide-react";
import { NAV_LINKS } from "@/data/site";

const Logo = () => (
  <a href="#home" data-testid="nav-logo" className="flex items-center gap-3">
    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy-950 font-display text-lg font-semibold text-electric-bright ring-1 ring-white/10">
      P
    </span>
    <span className="font-display text-lg font-medium tracking-tight text-white">
      Prata <span className="text-electric-bright">Technologies</span>
    </span>
  </a>
);

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goSection = (href) => (e) => {
    setOpen(false);
    if (location.pathname !== "/") {
      e.preventDefault();
      navigate("/" + href);
    }
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-50 w-full transition-[background-color,border-color,padding] duration-300 ${
        scrolled || location.pathname !== "/"
          ? "border-b border-white/10 bg-navy-950/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div
        className={`mx-auto flex max-w-7xl items-center justify-between px-6 transition-[padding] duration-300 md:px-12 ${
          scrolled ? "py-3" : "py-5"
        }`}
      >
        <Logo />
        <nav aria-label="Primary" className="hidden items-center gap-7 lg:flex">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={goSection(link.href)}
              data-testid={`nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm text-slate-300 transition-colors duration-300 hover:text-white"
            >
              {link.label}
            </a>
          ))}
        </nav>
        <div className="hidden lg:block">
          <a
            href="#contact"
            onClick={goSection("#contact")}
            data-testid="nav-book-consultation-btn"
            className="group inline-flex items-center gap-2 rounded-full bg-electric px-6 py-2.5 text-sm font-medium text-white transition-[background-color,box-shadow,transform] duration-300 hover:scale-[1.02] hover:bg-electric-bright hover:shadow-[0_0_24px_rgba(37,99,235,0.45)] active:scale-95"
          >
            Book a Consultation
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
          </a>
        </div>
        <button
          data-testid="mobile-menu-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center rounded-lg text-white ring-1 ring-white/15 lg:hidden"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="mobile-menu"
            aria-label="Mobile"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-b border-white/10 bg-navy-950/95 backdrop-blur-xl lg:hidden"
          >
            <div className="space-y-1 px-6 py-6">
              {NAV_LINKS.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={goSection(link.href)}
                  data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  className="block rounded-lg px-3 py-3 font-display text-lg text-slate-200 transition-colors duration-200 hover:bg-white/5 hover:text-white"
                >
                  {link.label}
                </motion.a>
              ))}
              <motion.div
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * NAV_LINKS.length }}
              >
                <Link
                  to="/careers"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-link-careers"
                  className="block rounded-lg px-3 py-3 font-display text-lg text-slate-200 transition-colors duration-200 hover:bg-white/5 hover:text-white"
                >
                  Careers
                </Link>
              </motion.div>
              <a
                href="#contact"
                onClick={goSection("#contact")}
                data-testid="mobile-book-consultation-btn"
                className="mt-4 flex items-center justify-center gap-2 rounded-full bg-electric px-6 py-3.5 font-medium text-white transition-colors duration-300 hover:bg-electric-bright"
              >
                Book a Consultation
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
